import { announce } from "@kovernow/flows/announce";
import { createSignInFlow, type SignInFlow, type SignInState } from "@kovernow/flows/auth";
import { Button } from "@kovernow/react-ui/components/button";
import { useEffect, useMemo, useRef, useState, useSyncExternalStore, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { useServices } from "../core/app-context";
import { useMutationGate } from "../core/mutation-gate";
import { useT } from "../core/use-t";
import { FormField } from "../components/FormField";
import { GatedButton } from "../components/GatedButton";
import { OtpStep } from "../components/OtpStep";
import { Page } from "../components/Page";
import { PasswordField } from "../components/PasswordField";
import { Recovery } from "../components/Recovery";
import { safeReturnTo } from "../core/safe-path";
import { useOnLeave } from "../core/use-on-leave";

export { safeReturnTo } from "../core/safe-path";

const STEP_TITLES: Record<SignInState["step"], string> = {
  credentials: "auth.signIn.title",
  otp: "auth.otp.title",
  unverified: "auth.unverified.title",
  notFound: "auth.notFound.title",
  accountExists: "auth.accountExists.title",
  done: "auth.signIn.title",
};

/**
 * Sign-in screens (CAP-3, CAP-5): rendered only from the flows sign-in step
 * machine; this file holds no auth logic.
 */
export function SignInScreen({ flowOverride }: { flowOverride?: SignInFlow }) {
  const services = useServices();
  const [params] = useSearchParams();
  const initialEmail = params.get("email") ?? undefined;
  const flow = useMemo(
    () =>
      flowOverride ??
      createSignInFlow({
        identity: services.identity,
        api: services.authApi,
        session: services.session,
        recovery: { hasMessage: (k) => services.i18n.exists(k) },
        initialEmail,
      }),
    // The prefill only seeds a new flow; it must not rebuild one mid-sign-in.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [flowOverride, services],
  );
  const state = useSyncExternalStore(flow.subscribe, flow.getState);
  const t = useT();
  const navigate = useNavigate();
  const returnTo = safeReturnTo(params.get("returnTo"));

  // A Google/Apple identity with no account stays signed in only while it's
  // being handed to sign-up; leaving any other way signs it out.
  const handedOff = useRef(false);
  const latest = useRef(state);
  latest.current = state;
  useOnLeave(() => {
    if (latest.current.social && !handedOff.current) void flow.abandonSocial();
  });

  useEffect(() => {
    void flow.resumeRedirect();
  }, [flow]);

  useEffect(() => {
    if (state.step === "done") {
      announce(t("auth.signedIn"));
      navigate(returnTo, { replace: true });
    }
  }, [state.step, navigate, returnTo, t]);

  return (
    <Page
      titleKey={STEP_TITLES[state.step]}
      flowKey="flows.signIn"
      focusKey={state.step === "credentials" ? undefined : state.step}
      className="mx-auto w-full max-w-md px-4 py-8"
    >
      <div aria-live="polite">
        {state.error ? (
          <Recovery
            live={false}
            action={state.error}
            onRetry={() => flow.dismissError()}
            handlers={{ resend_code: () => void flow.resendOtp() }}
          />
        ) : null}
      </div>
      <div className="mt-4">
        {state.step === "credentials" || state.step === "done" ? <Credentials flow={flow} state={state} /> : null}
        {state.step === "otp" ? (
          <OtpStep
            intro={t("auth.otp.intro", { email: state.email })}
            otpResetCount={state.otpResetCount}
            busy={state.busy}
            error={state.fieldErrors.otp ? t(state.fieldErrors.otp) : undefined}
            resendAvailableAt={state.resendAvailableAt}
            onSubmit={(code) => void flow.submitOtp(code)}
            onResend={() => void flow.resendOtp()}
            onBack={() => flow.back()}
            backLabel={t("auth.otp.useDifferentEmail")}
          />
        ) : null}
        {state.step === "unverified" ? <Unverified flow={flow} state={state} /> : null}
        {state.step === "notFound" ? (
          <NotFound
            flow={flow}
            state={state}
            onSignUp={() => {
              handedOff.current = true;
              navigate("/sign-up", { state: state.social ? { social: state.social } : undefined });
            }}
          />
        ) : null}
        {state.step === "accountExists" ? <AccountExists flow={flow} /> : null}
      </div>
    </Page>
  );
}

function Credentials({ flow, state }: { flow: SignInFlow; state: SignInState }) {
  const t = useT();
  const gate = useMutationGate();
  const [email, setEmail] = useState(state.email);
  const [password, setPassword] = useState("");
  const passwordFlagged = state.error?.fields?.includes("password");
  const emailFlagged = state.error?.fields?.includes("email");

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (gate.blocked || state.busy) return;
    void flow.submitCredentials(email, password);
  };

  return (
    <div className="flex flex-col gap-6">
      <p className="text-muted-foreground">{t("auth.signIn.intro")}</p>
      <form noValidate onSubmit={submit} className="flex flex-col gap-4">
        <FormField
          label={t("auth.signIn.email")}
          name="email"
          type="email"
          autoComplete="username"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onBlur={() => email && flow.validateField("email", email)}
          error={state.fieldErrors.email ? t(state.fieldErrors.email) : undefined}
          aria-invalid={emailFlagged || state.fieldErrors.email ? true : undefined}
        />
        <PasswordField
          label={t("auth.signIn.password")}
          name="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          error={state.fieldErrors.password ? t(state.fieldErrors.password) : undefined}
          aria-invalid={passwordFlagged || state.fieldErrors.password ? true : undefined}
        />
        <div className="flex justify-end">
          <Link to={email ? `/reset-password?email=${encodeURIComponent(email)}` : "/reset-password"} className="inline-flex min-h-11 items-center text-sm font-semibold text-primary underline">
            {t("auth.signIn.forgotPassword")}
          </Link>
        </div>
        <GatedButton type="submit" variant="cta" busy={state.busy} label={t("auth.signIn.submit")} />
      </form>
      <div className="flex items-center gap-3 text-sm text-muted-foreground" aria-hidden="true">
        <span className="h-px flex-1 bg-border" />
        {t("auth.signIn.or")}
        <span className="h-px flex-1 bg-border" />
      </div>
      <div className="flex flex-col gap-3">
        <GatedButton variant="outline" label={t("auth.signIn.google")} onClick={() => void flow.signInWithGoogle()} />
        <GatedButton variant="outline" label={t("auth.signIn.apple")} onClick={() => void flow.signInWithApple()} />
      </div>
      <p className="text-sm">
        {t("auth.signIn.noAccount")}{" "}
        <Link to="/sign-up" className="font-semibold text-primary underline">
          {t("auth.signIn.signUpLink")}
        </Link>
      </p>
    </div>
  );
}

function Unverified({ flow, state }: { flow: SignInFlow; state: SignInState }) {
  const t = useT();
  return (
    <div className="flex flex-col gap-6">
      <p>{t("auth.unverified.body", { email: state.email })}</p>
      <GatedButton variant="cta" busy={state.busy} label={t("auth.unverified.resend")} onClick={() => void flow.resendVerification()} />
      <Button type="button" variant="link" className="self-start px-0" onClick={() => flow.back()}>
        {t("common.back")}
      </Button>
    </div>
  );
}

function NotFound({ flow, state, onSignUp }: { flow: SignInFlow; state: SignInState; onSignUp: () => void }) {
  const t = useT();
  return (
    <div className="flex flex-col gap-6">
      <p>{t("auth.notFound.body")}</p>
      <Button type="button" variant="cta" size="touch" onClick={onSignUp} data-social={state.social ? "true" : undefined}>
        {t("auth.notFound.signUp")}
      </Button>
      <Button type="button" variant="link" className="self-start px-0" onClick={() => flow.back()}>
        {t("common.back")}
      </Button>
    </div>
  );
}

function AccountExists({ flow }: { flow: SignInFlow }) {
  const t = useT();
  return (
    <div className="flex flex-col gap-6">
      <p>{t("auth.accountExists.body")}</p>
      <Button type="button" variant="cta" size="touch" onClick={() => flow.back()}>
        {t("common.back")}
      </Button>
    </div>
  );
}
